import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Phone, MessageSquare, Mail, Send, CheckCircle2, DollarSign, Briefcase,
  Gauge, Gift, TrendingUp, Users, Target, Trophy, ArrowDown, AlertTriangle, Star, BookOpen, GraduationCap, RefreshCw, MailOpen, MousePointerClick,
} from "lucide-react";
import api from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { KpiCard } from "@/components/KpiCard";
import { StageBadge } from "@/components/StageBadge";
import { Button } from "@/components/ui/button";
import { DateRangeControl } from "@/components/DateRangeControl";
import { PageHeader, SectionHeader, Spinner } from "@/components/ui/page";
import { fmtCurrency, fmtNumber, fmtPct, presetRange } from "@/lib/format";

export default function Dashboard() {
  const navigate = useNavigate();
  const { selected, currentClient, isAll } = useClient();
  const { user } = useAuth();
  const isAgent = user?.role === "team_member" || user?.role === "super_admin";
  const [range, setRange] = useState(() => {
    const r = presetRange("last30");
    return { preset: "last30", start: r.start, end: r.end };
  });

  const startISO = new Date(range.start).toISOString();
  const endISO = new Date(range.end).toISOString();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", selected, startISO, endISO],
    queryFn: async () =>
      (await api.get("/dashboard", { params: { client_id: selected, start: startISO, end: endISO } })).data,
    enabled: !!selected,
  });

  const { data: counts } = useQuery({
    queryKey: ["contact-counts", selected],
    queryFn: async () => (await api.get(`/clients/${selected}/contacts/counts`)).data,
    enabled: !!selected && !isAll,
  });

  const { data: priority } = useQuery({
    queryKey: ["priority-projects", selected],
    queryFn: async () => (await api.get(`/clients/${selected}/contacts`, { params: { priority: "true", page_size: 50 } })).data,
    enabled: !!selected && !isAll,
  });

  const { data: brevo } = useQuery({
    queryKey: ["brevo-summary", selected],
    queryFn: async () => (await api.get(`/clients/${selected}/brevo/summary`)).data,
    enabled: !!selected && !isAll,
  });

  const downloadGuide = async () => {
    const res = await api.get("/client-guide", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "vsm-client-guide.pdf"; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTeamGuide = async () => {
    const res = await api.get("/team-guide", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "vsm-agent-guide.pdf"; a.click();
    URL.revokeObjectURL(url);
  };

  const m = data?.metrics || {};
  const p = data?.previous || {};

  const q = useMemo(() => `?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`, [startISO, endISO]);
  const go = (path) => navigate(path);

  if (!selected) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle={isAll ? "Aggregate across all VSM clients" : currentClient ? `${currentClient.business_name} · ${currentClient.industry || "VSM Client"}` : "VSM Performance"}
      >
        <Button variant="outline" onClick={downloadGuide} data-testid="client-guide-btn">
          <BookOpen className="h-4 w-4 mr-1.5" /> Client Guide (PDF)
        </Button>
        {isAgent && (
          <Button variant="outline" onClick={downloadTeamGuide} data-testid="agent-guide-btn">
            <GraduationCap className="h-4 w-4 mr-1.5" /> Agent Guide (PDF)
          </Button>
        )}
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-2">
        <DateRangeControl value={range} onChange={setRange} />
      </div>

      {!isAll && counts?.STALE > 0 && (
        <button onClick={() => navigate("/contacts?stale=true")} data-testid="stale-alert-banner"
          className="w-full mb-2 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left hover:bg-amber-100 transition-colors">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <span className="text-sm text-amber-800 font-medium flex-1">{counts.STALE.toLocaleString()} contacts need attention — no activity in 14+ days.</span>
          <span className="text-sm font-semibold text-amber-700 whitespace-nowrap">Work the list →</span>
        </button>
      )}

      {!isAll && priority?.items?.length > 0 && (
        <div className="mb-4">
          <SectionHeader>Priority Projects</SectionHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {priority.items.map((c) => (
              <button key={c.id} onClick={() => navigate(`/contacts/${c.id}`)} data-testid={`priority-card-${c.id}`}
                className="text-left bg-white border border-amber-200 rounded-xl p-4 hover:border-amber-300 hover:shadow-sm transition-all">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400 shrink-0" />
                    <span className="truncate">{c.first_name} {c.last_name}</span>
                  </div>
                  <StageBadge stage={c.current_stage} />
                </div>
                <div className="text-xs text-slate-500 mt-1 truncate">{c.mobile_phone || c.email || "—"}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading ? <Spinner /> : (
        <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.03 } } }}>
          {/* RE-ENGAGED (VSM) SHOWCASE BOARD */}
          <button
            data-testid="reengaged-showcase-board"
            onClick={() => go(`/contacts?stage=RE_ENGAGED`)}
            className="w-full mb-4 text-left rounded-2xl border border-violet-200 bg-gradient-to-r from-violet-50 to-white p-5 hover:border-violet-300 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                <RefreshCw className="h-6 w-6 text-violet-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-violet-600">Re-Engaged (VSM)</div>
                <div className="text-sm text-slate-500">Dormant leads the VSM team brought back to life</div>
              </div>
              <div className="flex items-center gap-8 shrink-0">
                <div className="text-right">
                  <div className="text-3xl font-bold tabular-nums text-slate-900" data-testid="reengaged-current-value">{fmtNumber(m.re_engaged_count)}</div>
                  <div className="text-xs text-slate-500">currently re-engaged</div>
                </div>
                <div className="hidden sm:block text-right border-l border-violet-100 pl-8">
                  <div className="text-3xl font-bold tabular-nums text-violet-700" data-testid="reengaged-period-value">{fmtNumber(m.moved_to_re_engaged)}</div>
                  <div className="text-xs text-slate-500">re-engaged this period</div>
                </div>
              </div>
            </div>
          </button>

          {/* SECTION 1 — VSM VALUE */}
          <SectionHeader>1 · VSM Value & Signed Revenue</SectionHeader>
          <Grid>
            <KpiCard testid="kpi-signed-deal-value" label="Signed Deal Value" icon={DollarSign} accent="emerald"
              value={fmtCurrency(m.signed_deal_value)} cur={m.signed_deal_value} prev={p.signed_deal_value}
              onClick={() => go(`/deals${q}`)} footer={m.signed_deals ? `${m.signed_deals} deals` : "no deals in period"} />
            <KpiCard testid="kpi-signed-deals" label="Signed Deals" icon={Briefcase} accent="blue"
              value={fmtNumber(m.signed_deals)} cur={m.signed_deals} prev={p.signed_deals} onClick={() => go(`/deals${q}`)} />
            <KpiCard testid="kpi-avg-job-size" label="Average Job Size" icon={Gauge} accent="violet"
              value={fmtCurrency(m.avg_job_size)} showDelta={false}
              footer={m.signed_deals ? "actual blended" : "configured"} onClick={() => go(`/deals${q}`)} />
            <KpiCard testid="kpi-vsm-influenced-value" label="VSM Influenced Value" icon={TrendingUp} accent="emerald"
              value={fmtCurrency(m.vsm_influenced_value)} cur={m.vsm_influenced_value} prev={p.vsm_influenced_value}
              footer={`${fmtPct(m.vsm_influence_rate)} influence rate`} onClick={() => go(`/deals${q}`)} />
            <KpiCard testid="kpi-referrals-generated" label="Referrals Generated" icon={Gift} accent="amber"
              value={fmtNumber(m.referrals_generated)} cur={m.referrals_generated} prev={p.referrals_generated} onClick={() => go(`/referrals`)} />
          </Grid>

          {/* SECTION 2 — VSM ACTIVITY */}
          <SectionHeader>2 · VSM Sales Activity</SectionHeader>
          <Grid>
            <KpiCard testid="kpi-calls" label="Calls" icon={Phone} accent="blue" value={fmtNumber(m.calls_count)}
              cur={m.calls_count} prev={p.calls_count} onClick={() => go(`/activity${q}&type=CALL`)} />
            <KpiCard testid="kpi-texts" label="Texts" icon={MessageSquare} accent="violet" value={fmtNumber(m.texts_count)}
              cur={m.texts_count} prev={p.texts_count} onClick={() => go(`/activity${q}&type=TEXT`)} />
            <KpiCard testid="kpi-emails" label="Emails" icon={Mail} accent="amber" value={fmtNumber(m.emails_count)}
              cur={m.emails_count} prev={p.emails_count} onClick={() => go(`/activity${q}&type=EMAIL`)} />
            <KpiCard testid="kpi-mailers" label="Mailers" icon={Send} accent="slate" value={fmtNumber(m.mailers_count)}
              cur={m.mailers_count} prev={p.mailers_count} onClick={() => go(`/activity${q}&type=MAILER`)} />
            <KpiCard testid="kpi-total-activities" label="Total Activities" icon={CheckCircle2} accent="emerald" value={fmtNumber(m.total_activities)}
              cur={m.total_activities} prev={p.total_activities} onClick={() => go(`/activity${q}`)} />
          </Grid>

          {/* SECTION 3 — PIPELINE */}
          <SectionHeader>3 · Sales Pipeline</SectionHeader>
          <Grid>
            <KpiCard testid="kpi-leads" label="Leads" icon={Users} accent="blue" value={fmtNumber(m.leads_count)}
              showDelta={false} footer="current stage" onClick={() => go(`/contacts?stage=LEAD`)} />
            <KpiCard testid="kpi-prospects" label="Prospects" icon={Target} accent="amber" value={fmtNumber(m.prospects_count)}
              showDelta={false} footer="current stage" onClick={() => go(`/contacts?stage=PROSPECT`)} />
            <KpiCard testid="kpi-reengaged" label="Re-Engaged (VSM)" icon={RefreshCw} accent="violet" value={fmtNumber(m.re_engaged_count)}
              showDelta={false} footer="current stage" onClick={() => go(`/contacts?stage=RE_ENGAGED`)} />
            <KpiCard testid="kpi-deals-pipeline" label="Deals" icon={Briefcase} accent="violet" value={fmtNumber(m.deals_current)}
              showDelta={false} footer="current stage" onClick={() => go(`/contacts?stage=DEAL`)} />
            <KpiCard testid="kpi-won-pipeline" label="Won" icon={Trophy} accent="emerald" value={fmtNumber(m.moved_to_won)}
              cur={m.moved_to_won} prev={p.moved_to_won} footer="won in period" onClick={() => go(`/contacts?stage=WON`)} />
            <KpiCard testid="kpi-lost" label="Lost" icon={ArrowDown} accent="red" value={fmtNumber(m.moved_to_lost)}
              cur={m.moved_to_lost} prev={p.moved_to_lost} higherIsBetter={false} footer="in period" onClick={() => go(`/contacts?stage=LOST`)} />
            <KpiCard testid="kpi-total-contacts" label="Total Contacts" icon={Users} accent="slate" value={fmtNumber(m.total_contacts)}
              showDelta={false} footer="all contacts" onClick={() => go(`/contacts`)} />
          </Grid>

          {/* SECTION 4 — CONVERSION */}
          <SectionHeader>4 · Conversion & Efficiency</SectionHeader>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard testid="kpi-lead-prospect" label="Lead → Prospect" accent="blue" value={fmtPct(m.lead_to_prospect_rate)}
              showDelta={false} footer="pipeline to date" onClick={() => go(`/contacts?stage=PROSPECT`)} />
            <KpiCard testid="kpi-prospect-deal" label="Prospect → Deal" accent="emerald" value={fmtPct(m.prospect_to_deal_rate)}
              showDelta={false} footer="pipeline to date" onClick={() => go(`/deals${q}`)} />
            <KpiCard testid="kpi-lead-deal" label="Lead → Deal" accent="violet" value={fmtPct(m.lead_to_deal_rate)}
              showDelta={false} footer="pipeline to date" onClick={() => go(`/deals${q}`)} />
            <KpiCard testid="kpi-active-pipeline" label="Active Pipeline Value" icon={DollarSign} accent="amber" value={fmtCurrency(m.active_pipeline_value)}
              showDelta={false} footer="active prospects" onClick={() => go(`/contacts?stage=PROSPECT`)} />
          </div>

          {/* SECTION 5 — FUNNEL MOVEMENT */}
          <SectionHeader>5 · Funnel Movement (this period)</SectionHeader>
          <FunnelMovement m={m} onGo={go} q={q} />

          {/* SECTION 6 — REFERRALS */}
          <SectionHeader>6 · Referral Program</SectionHeader>
          <Grid>
            <KpiCard testid="kpi-ref-customers" label="Customers in Program" icon={Gift} accent="amber" value={fmtNumber(m.customers_in_program)}
              showDelta={false} onClick={() => go(`/referrals`)} />
            <KpiCard testid="kpi-ref-requests" label="Referral Requests" accent="blue" value={fmtNumber(m.referral_requests)}
              cur={m.referral_requests} prev={p.referral_requests} onClick={() => go(`/referrals`)} />
            <KpiCard testid="kpi-ref-generated" label="Referrals Generated" accent="violet" value={fmtNumber(m.referrals_generated)}
              cur={m.referrals_generated} prev={p.referrals_generated} onClick={() => go(`/referrals`)} />
            <KpiCard testid="kpi-ref-deals" label="Referral Deals" icon={Trophy} accent="emerald" value={fmtNumber(m.referral_deals)}
              cur={m.referral_deals} prev={p.referral_deals} onClick={() => go(`/referrals`)} />
            <KpiCard testid="kpi-ref-revenue" label="Referral Revenue" icon={DollarSign} accent="emerald" value={fmtCurrency(m.referral_revenue)}
              cur={m.referral_revenue} prev={p.referral_revenue} onClick={() => go(`/referrals`)} />
          </Grid>

          {/* SECTION 7 — EMAIL MARKETING (BREVO) */}
          {!isAll && brevo?.configured && (
            <>
              <SectionHeader>7 · Email Marketing (Brevo)</SectionHeader>
              {brevo.ok ? (
                <Grid>
                  <KpiCard testid="kpi-brevo-sent" label="Emails Sent" icon={Send} accent="blue" value={fmtNumber(brevo.totals.sent)}
                    showDelta={false} footer={`${brevo.count} campaigns`} />
                  <KpiCard testid="kpi-brevo-delivered" label="Delivered" icon={Mail} accent="slate" value={fmtNumber(brevo.totals.delivered)} showDelta={false} />
                  <KpiCard testid="kpi-brevo-opens" label="Unique Opens" icon={MailOpen} accent="violet" value={fmtNumber(brevo.totals.unique_opens)}
                    showDelta={false} footer={`${fmtPct(brevo.totals.open_rate)} open rate`} />
                  <KpiCard testid="kpi-brevo-clicks" label="Unique Clicks" icon={MousePointerClick} accent="emerald" value={fmtNumber(brevo.totals.unique_clicks)}
                    showDelta={false} footer={`${fmtPct(brevo.totals.click_rate)} click rate`} />
                  <KpiCard testid="kpi-brevo-bounces" label="Bounces" icon={ArrowDown} accent="red" value={fmtNumber(brevo.totals.bounces)}
                    showDelta={false} higherIsBetter={false} footer={`${fmtNumber(brevo.totals.unsubscriptions)} unsubscribed`} />
                </Grid>
              ) : (
                <div className="bg-white border border-amber-200 rounded-xl p-4 text-sm text-amber-700" data-testid="brevo-error">
                  Couldn't load Brevo data: {brevo.error}
                </div>
              )}
            </>
          )}
          <div className="h-6" />
        </motion.div>
      )}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">{children}</div>;
}

function FunnelMovement({ m, onGo, q }) {
  const steps = [
    { label: "New Leads Added", value: m.new_leads, color: "bg-blue-600", to: `/contacts?stage=LEAD` },
    { label: "Moved to Prospect", value: m.moved_to_prospect, color: "bg-amber-500", to: `/contacts?stage=PROSPECT` },
    { label: "Re-Engaged (VSM)", value: m.moved_to_re_engaged, color: "bg-violet-500", to: `/contacts?stage=RE_ENGAGED` },
    { label: "Signed Deals", value: m.moved_to_deal, color: "bg-emerald-600", to: `/deals${q}` },
    { label: "Moved to Lost", value: m.moved_to_lost, color: "bg-slate-400", to: `/contacts?stage=LOST` },
  ];
  const max = Math.max(...steps.map((s) => s.value || 0), 1);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="space-y-3">
        {steps.map((s, i) => (
          <button key={i} onClick={() => onGo(s.to)} data-testid={`funnel-${i}`}
            className="w-full flex items-center gap-4 group">
            <div className="w-36 text-right text-sm font-medium text-slate-600 shrink-0">{s.label}</div>
            <div className="flex-1 h-9 bg-slate-100 rounded-lg overflow-hidden relative">
              <motion.div initial={{ width: 0 }} animate={{ width: `${((s.value || 0) / max) * 100}%` }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`h-full ${s.color} rounded-lg min-w-[2px]`} />
              <span className="absolute inset-y-0 left-3 flex items-center text-sm font-bold text-slate-900 tabular-nums">
                {fmtNumber(s.value)}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
